// src/features/shared/components/LoadingScreen.tsx
import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingScreenProps {
  fullScreen?: boolean;
  message?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  fullScreen = true,
  message,
  size = 'medium',
  className = '',
}) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  };

  if (fullScreen) {
    return (
      <div className={`min-h-screen bg-muted flex items-center justify-center ${className}`}>
        <div className="text-center">
          <Loader2 
            className={`${sizeClasses[size]} text-orange-500 animate-spin mx-auto`} 
          />
          {message && (
            <p className={`mt-4 text-muted-foreground ${textSizeClasses[size]}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
      <Loader2 
        className={`${sizeClasses[size]} text-orange-500 animate-spin`} 
      />
      {message && (
        <p className={`mt-4 text-muted-foreground ${textSizeClasses[size]}`}>
          {message}
        </p>
      )}
    </div>
  );
};

// Inline loading spinner component
export const LoadingSpinner: React.FC<{
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <Loader2 
      className={`${sizeClasses[size]} animate-spin ${className}`} 
    />
  );
};

// Loading overlay component
export const LoadingOverlay: React.FC<{
  visible: boolean;
  message?: string;
  children: React.ReactNode;
}> = ({ visible, message, children }) => {
  return (
    <div className="relative">
      {children}
      {visible && (
        <div className="absolute inset-0 bg-card bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
            {message && (
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Button with loading state
export const LoadingButton: React.FC<{
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({
  loading = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  className = '',
}) => {
  const baseClasses = 'font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center';
  
  const variantClasses = {
    primary: 'bg-orange-500 text-white hover:bg-orange-600 focus:ring-orange-500 disabled:bg-orange-300',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 disabled:bg-gray-400',
    outline: 'border border-border text-foreground hover:bg-muted focus:ring-orange-500 disabled:bg-muted',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} ${
        (disabled || loading) ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      {loading && <LoadingSpinner size="sm" className="mr-2" />}
      {children}
    </button>
  );
};

export default LoadingScreen;